import { getDefaultAuthenticationProviderSelectorProperties } from '../model/AuthenticationProviderSelectorProperties';
import { ListResult } from '../../../ts/fetchers/ListResult';
import { CommonErrorCode } from '../../../ts/helpers/ErrorCodes';
import {
  IErrorItem,
  MultipleItemResult
} from '../../../commonInfra/contentActions/MultipleItemResult';
import { Set } from '../../../ts/models/Set';
import { SiteRolesResource } from '../../../userManagement/services/resources/SiteRoles';
import { SiteUsers } from '../../../userManagement/services/resources/SiteUsers';
import { IError } from '../../../httpClient/serverApi/types/IError';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { ISiteUser } from '../../../ts/ServerApi/types/ISiteUser';
import { ContentActionNotification} from '../../../commonInfra/contentActions/ContentActionNotification';
import {
  AuthenticationProviderType,
  IAuthenticationProvider,
  is3rdParty,
  SiteAuthenticationSettingsService
} from './SiteAuthenticationSettingsService';
import { ServerSettingsUnauthenticated } from '../../services/ServerSettingsUnauthenticated';
import { createMultipleItemResultNotificationValues } from '../../../commonInfra/contentActions/ActionMessages';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { Messages, Formatters } from '../../../codegen/Localize';
import { TabDialogWidth } from '../../../commonInfra/dialogs/style/TabDialogWidth';
import {
  UpdateUserAuthenticationDialogBody,
  UpdateUserAuthenticationDialogProps,
  UpdateUserAuthenticationDialogState,
  UpdateUserAuthenticationWarningDialog
} from '../components/UpdateUserAuthenticationDialog';
import { openReactActionDialog } from '../../../commonInfra/dialogs/ReactActionDialog';
import { openReactConfirmationDialog } from '../../../commonInfra/dialogs/ReactConfirmationDialog';

export namespace UpdateUserAuthenticationAction {

  export async function showUpdateUserAuthenticationDialog(siteUsersToUpdate: Set<ISiteUser>): Promise<any> {
    const getProviderDescription = (provider: IAuthenticationProvider) => {
      const translateValues = {
        SERVER_NAME: ServerSettingsUnauthenticated.serverName(),
        PROVIDER_NAME: SiteAuthenticationSettingsService.getProviderDisplayName(provider),
      };

      switch(provider.type){
        case AuthenticationProviderType.default:
          return Messages.setUsersAuthentication_description_internal(translateValues);
        case AuthenticationProviderType.tabidmfa:
          return Messages.setUsersAuthentication_description_tableauid_mfa(translateValues);
        default:
          return Messages.setUsersAuthentication_description_external(translateValues);
      }
    };

    const authenticationProviderSelectorProperties = await getAuthenticationProvider(siteUsersToUpdate, getProviderDescription);

    const dialogInstance = openReactActionDialog<UpdateUserAuthenticationDialogProps, UpdateUserAuthenticationDialogState, any>({
      title: Messages.updateUserAuthenticationTitle(),
      bodyComponent: UpdateUserAuthenticationDialogBody,
      initialBodyProps: {
        currentUser: siteUsersToUpdate.get(getSessionManagementServiceInstance().sessionUserInfo().id),
        isAboutToBeLoggedOut: false
      },
      initialDialogState: {...authenticationProviderSelectorProperties},
      actionName: Messages.update(),
      action: (bodyProps: UpdateUserAuthenticationDialogProps, dialogState: UpdateUserAuthenticationDialogState) => {
        const provider = dialogState.authenticationProvider;
        const userIds = siteUsersToUpdate.map(user => user.id);
        const updateUserAuthentication = () => {
          const promise = SiteAuthenticationSettingsService.setProviderIdForUsers(provider.id, userIds);
          showNotification(siteUsersToUpdate, promise);
          return promise;
        };

        return determineIfNoMoreAdminsUsingDefaultProvider(siteUsersToUpdate, provider).then((showWarning: boolean) => {
          if (showWarning) {
            const warningDialog = openReactConfirmationDialog<void, any>({
              title: Messages.updateUserAuthenticationTitle(),
              message: '',
              actionName: Messages.update(),
              action: updateUserAuthentication,
              paramsForAction: undefined,
              isActionDelete: true,
              bodyComponent: UpdateUserAuthenticationWarningDialog,
              dialogWidth: TabDialogWidth.width375px
            });
            return Promise.resolve(warningDialog.result);
          } else {
            return updateUserAuthentication();
          }
        });
      },
      dialogWidth: TabDialogWidth.width375px,
      dismissDialogOnActionFailed: false
    });

    return Promise.resolve(dialogInstance.result);
  }

  async function getAuthenticationProvider(siteUsersToUpdate: Set<ISiteUser>, getProviderDescription: (provider: IAuthenticationProvider) => string) {
    const settings = await SiteAuthenticationSettingsService.getSiteAuthenticationSettings();
    const authenticationProvider = SiteAuthenticationSettingsService.findProvider(settings, siteUsersToUpdate.first().authenticationProviderId);
    const defaultAuthenticationProviderSelectorProperties = getDefaultAuthenticationProviderSelectorProperties();

    return {
      ...defaultAuthenticationProviderSelectorProperties,
      authenticationProviderIds: siteUsersToUpdate.map(user => user.authenticationProviderId),
      authenticationProvider: authenticationProvider,
      getProviderDescription: getProviderDescription};
  }

  function showNotification(siteUsersToUpdate: Set<ISiteUser>, promise: Promise<IResult>) {
    promise.catch(() => {
      ContentActionNotification.displayFailureToast(Messages.unexpectedError());
    });

    promise.then((result: IResult) => {
      const multipleItemResult = new MultipleItemResult(siteUsersToUpdate, result);
      ContentActionNotification.displayItemizedToastsForResult(createMultipleItemResultNotificationValues(multipleItemResult,
        {
          describeSuccessSummary: (successCount: number) =>
            Messages.updateUserAuthenticationSuccessSummary({COUNT: successCount, COUNT_LOC: Formatters.formatCount(successCount)}),

          describeErrorSummary: (errorCount: number) =>
            Messages.updateUserAuthenticationErrorSummary({COUNT: errorCount, COUNT_LOC: Formatters.formatCount(errorCount)}),

          describeErrorDetail: (errorItem: IErrorItem<ISiteUser>) => {
            return {
              itemName: errorItem.item && errorItem.item.displayName,
              itemUrl: null,
              errorMessage: getErrorDetailText(errorItem.error)
            };
          }
        }));
    });
  }

  function getErrorDetailText(error: IError) {
    switch(error.code) {
      case CommonErrorCode.SiteSAMLUserExistsOnAnotherSite:
        return Messages.userNameExistsOnServerForUpdateAuthenticationToSiteSAML();
      case CommonErrorCode.SystemUserCannotBeSiteSAMLUser:
        return Messages.systemAdminNotAllowedAsSiteSAMLUser();
      case CommonErrorCode.GuestUserCannotBeSiteSAMLUser:
        return Messages.guestUserNotAllowedAsSiteSAMLUser();
      default:
        return Messages.unexpectedError();
    }
  }

  function determineIfNoMoreAdminsUsingDefaultProvider(siteUsersToUpdate: Set<ISiteUser>, newAuthenticationProvider: IAuthenticationProvider): Promise<boolean> {
    const defaultAuthenticationProviderId = SiteAuthenticationSettingsService.getDefaultProvider().id;
    const authenticatedByDefaultProvider = (user: ISiteUser) => user.authenticationProviderId === defaultAuthenticationProviderId;

    if (!is3rdParty(newAuthenticationProvider.id)) {
      return Promise.resolve(false);
    }

    return SiteUsers.instance.getUsersBySiteRoles(SiteRolesResource.instance.getAdminRoles()).then((result: ListResult<ISiteUser>) => {
      let adminsUsingDefaultProviderBefore = Set.fromIdItemArray(result.items).filter(authenticatedByDefaultProvider);
      let adminsUsingDefaultProviderAfter = adminsUsingDefaultProviderBefore.clone();
      adminsUsingDefaultProviderAfter.removeItemsIn(siteUsersToUpdate);
      return !adminsUsingDefaultProviderBefore.isEmpty() && adminsUsingDefaultProviderAfter.isEmpty();
    });
  }
}

export type UpdateUserAuthenticationAction = typeof UpdateUserAuthenticationAction;
